import { motion } from 'motion/react'
import { useState } from 'react'

const PIECE_COUNT = 90
const SPREAD_PIXELS = 560
const RISE_MIN_PIXELS = 140
const RISE_RANGE_PIXELS = 260
const FALL_MIN_PIXELS = 520
const FALL_RANGE_PIXELS = 360
const FLIGHT_MIN_SECONDS = 0.9
const FLIGHT_RANGE_SECONDS = 0.5
const LAUNCH_SPREAD_SECONDS = 0.15
const PEAK_AT = 0.28
const MOST_SPIN_DEGREES = 540
const COLORS = [
  'hsl(var(--brand))',
  'hsl(var(--chart-1))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-7))',
  'hsl(var(--chart-8))',
]
const SHAPES = ['h-2.5 w-1.5 rounded-[1px]', 'size-2 rounded-full']

interface ConfettiPiece {
  id: number
  drift: number
  rise: number
  fall: number
  spin: number
  seconds: number
  delay: number
  color: string
  shape: string
}

const pickFrom = <Choice,>(choices: Choice[]) =>
  choices[Math.floor(Math.random() * choices.length)]

const throwPiece = (id: number): ConfettiPiece => ({
  id,
  drift: (Math.random() - 0.5) * SPREAD_PIXELS * 2,
  rise: RISE_MIN_PIXELS + Math.random() * RISE_RANGE_PIXELS,
  fall: FALL_MIN_PIXELS + Math.random() * FALL_RANGE_PIXELS,
  spin: (Math.random() - 0.5) * MOST_SPIN_DEGREES * 2,
  seconds: FLIGHT_MIN_SECONDS + Math.random() * FLIGHT_RANGE_SECONDS,
  delay: Math.random() * LAUNCH_SPREAD_SECONDS,
  color: pickFrom(COLORS),
  shape: pickFrom(SHAPES),
})

// Pieces shoot up from one point, spread out, then fall and fade past the card.
export const ConfettiBurst = () => {
  const [pieces] = useState(() =>
    Array.from({ length: PIECE_COUNT }, (_, id) => throwPiece(id)),
  )

  return (
    <div className="relative size-0">
      {pieces.map((piece) => (
        <motion.span
          key={piece.id}
          className={`absolute ${piece.shape}`}
          style={{ backgroundColor: piece.color }}
          initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
          animate={{
            x: [0, piece.drift * 0.7, piece.drift],
            y: [0, -piece.rise, piece.fall],
            rotate: piece.spin,
            opacity: [1, 1, 0],
          }}
          transition={{
            duration: piece.seconds,
            delay: piece.delay,
            times: [0, PEAK_AT, 1],
            ease: ['easeOut', 'easeIn'],
          }}
        />
      ))}
    </div>
  )
}
